import type { Task } from './models'
import { ancestorsOf } from './taskTree'
import { withoutSucceededRounds } from './taskFilters'
import { successorRule } from '../recurrence/end'
import { nextOccurrenceAfterCompletion } from '../recurrence/nextOccurrence'

/**
 * **Das letzte Kind schließt die Eltern-Zeile** (#217, Sven 29.09.2026 — kippt Grill 8 aus #181):
 * Wird `id` abgehakt und sind danach alle Kinder ihrer Eltern-Zeile erledigt, ist die es auch —
 * Ebene für Ebene hinauf. Liefert die Zeilen, die dadurch zugehen, von unten nach oben.
 *
 * Es hält an
 * - an der **Aufgabe**: Sie schließt nie von allein, dort zeigt der Balken, wie weit sie ist;
 * - an einer **wiederkehrenden** Zeile: Die hakt man selbst ab, das bringt ihre nächste Runde;
 * - wenn `id` selbst **wiederkehrt**: Ihre nächste Runde steht gleich offen da — außer es war die
 *   letzte ihrer Serie, dann zählt sie wie jede andere Zeile.
 *
 * Gezählt wird wie im Fortschritt daneben: lebende Kinder ohne abgelöste Runden.
 * `tree`: alle Zeilen der Aufgabe samt Kette über `id`, auf dem Stand NACH dem Abhaken.
 */
export function ancestorsCompletedBy(tree: readonly Task[], id: string): string[] {
  const row = tree.find(t => t.id === id)
  if (!row || row.status !== 'completed' || bringsNextRound(row)) return []
  const closed = new Set([id])
  const result: string[] = []
  for (const ancestor of ancestorsOf(tree, id)) {
    if (ancestor.parentTaskId == null || ancestor.deletedAt || ancestor.recurrence != null || ancestor.status === 'completed') break
    const children = withoutSucceededRounds(tree.filter(t => t.parentTaskId === ancestor.id && !t.deletedAt), tree)
    if (!children.every(child => child.status === 'completed' || closed.has(child.id))) break
    closed.add(ancestor.id)
    result.push(ancestor.id)
  }
  return result
}

/** Dieselbe Rechnung wie `spawnNextOccurrence`: Legt das Abhaken von `row` eine nächste Runde an? */
function bringsNextRound(row: Task): boolean {
  if (row.recurrence == null) return false
  const completedAt = row.completedAt ?? new Date()
  const next = nextOccurrenceAfterCompletion(row.recurrence, { startDate: row.startDate, completedAt })
  return successorRule(row.recurrence, next, completedAt) != null
}
